<template>
  <teleport to="body">
    <div
      v-if="show"
      class="bsm-overlay asset-action-modal-overlay"
      role="dialog"
      aria-modal="true"
      :aria-label="title"
      @click.self="emit('close')"
    >
      <div class="bsm asset-action-modal asset-action-modal--button-style">
        <!-- Header -->
        <header class="bsm__header modal-header">
          <div class="bsm__header-copy">
            <h3>{{ title }}</h3>
            <p v-if="description" class="bsm__description">{{ description }}</p>
          </div>
          <button
            type="button"
            class="bsm__close modal-close-button"
            aria-label="Close button editor"
            @click="emit('close')"
          >
            &times;
          </button>
        </header>

        <!-- Sticky live preview -->
        <div class="bsm__preview-sticky">
          <div class="bsm__preview-shell" :style="previewSurfaceStyle">
            <button type="button" class="bsm__preview-button" :style="previewStyle">
              {{ previewLabel }}
            </button>
          </div>
        </div>

        <!-- Scrollable options body -->
        <div class="bsm__scroll-body modal-body">
          <!-- Group: Button URL (prize screen only) -->
          <fieldset v-if="allowUrl" class="bsm__group">
            <legend class="bsm__group-title">Content</legend>
            <div class="bsm__field-grid">
              <label class="bsm__field bsm__field--wide">
                <span class="bsm__field-label">Button URL</span>
                <input
                  v-model.trim="draft.url"
                  type="text"
                  inputmode="url"
                  placeholder="example.com"
                  :aria-invalid="Boolean(urlError)"
                  @input="validateUrl"
                  @blur="normalizeDraftUrl"
                />
              </label>
            </div>
            <p v-if="urlError" class="bsm__error">{{ urlError }}</p>
          </fieldset>

          <!-- Group: Style Variant -->
          <fieldset class="bsm__group">
            <legend class="bsm__group-title">Style Variant</legend>
            <div class="bsm__variant-pills">
              <button
                v-for="variant in STYLE_VARIANTS"
                :key="variant.value"
                type="button"
                class="bsm__variant-pill"
                :class="{ 'bsm__variant-pill--active': draft.styleVariant === variant.value }"
                :aria-pressed="draft.styleVariant === variant.value"
                @click="draft.styleVariant = variant.value"
              >
                {{ variant.label }}
              </button>
            </div>
          </fieldset>

          <!-- Group: Colours -->
          <fieldset class="bsm__group">
            <legend class="bsm__group-title">Colours</legend>
            <div class="bsm__field-grid">
              <label class="bsm__field">
                <span class="bsm__field-label">Text</span>
                <div class="bsm__color-row">
                  <input v-model="draft.textColor" type="color" class="bsm__color-swatch" />
                  <input
                    v-model="draft.textColor"
                    type="text"
                    class="bsm__color-hex"
                    maxlength="7"
                    placeholder="#ffffff"
                  />
                </div>
              </label>

              <label v-if="draft.styleVariant !== 'outline'" class="bsm__field">
                <span class="bsm__field-label">Background</span>
                <div class="bsm__color-row">
                  <input v-model="draft.backgroundColor" type="color" class="bsm__color-swatch" />
                  <input
                    v-model="draft.backgroundColor"
                    type="text"
                    class="bsm__color-hex"
                    maxlength="7"
                    placeholder="#000000"
                  />
                </div>
              </label>

              <label class="bsm__field">
                <span class="bsm__field-label">Border</span>
                <div class="bsm__color-row">
                  <input v-model="draft.borderColor" type="color" class="bsm__color-swatch" />
                  <input
                    v-model="draft.borderColor"
                    type="text"
                    class="bsm__color-hex"
                    maxlength="7"
                    placeholder="#000000"
                  />
                </div>
              </label>
            </div>
          </fieldset>

          <!-- Group: Shape -->
          <fieldset class="bsm__group">
            <legend class="bsm__group-title">Shape</legend>
            <div class="bsm__field-grid">
              <label class="bsm__field">
                <span class="bsm__field-label">Corner Radius</span>
                <div class="bsm__range-row">
                  <input v-model.number="draft.borderRadius" type="range" min="0" max="50" />
                  <strong class="bsm__range-value">{{ draft.borderRadius }}px</strong>
                </div>
              </label>

              <label v-if="draft.styleVariant === 'frosted'" class="bsm__field">
                <span class="bsm__field-label">Glass Blur</span>
                <div class="bsm__range-row">
                  <input v-model.number="draft.blurPx" type="range" min="0" max="24" />
                  <strong class="bsm__range-value">{{ draft.blurPx }}px</strong>
                </div>
              </label>
            </div>
          </fieldset>

          <!-- Group: Drop Shadow -->
          <fieldset class="bsm__group">
            <legend class="bsm__group-title">
              Drop Shadow
              <label class="bsm__toggle-label">
                <input
                  v-model="draftShadow.enabled"
                  type="checkbox"
                  class="bsm__toggle-checkbox"
                />
                <span class="bsm__toggle-switch" role="switch" :aria-checked="draftShadow.enabled" />
              </label>
            </legend>

            <div v-if="draftShadow.enabled" class="bsm__field-grid">
              <label class="bsm__field">
                <span class="bsm__field-label">Offset X</span>
                <div class="bsm__range-row">
                  <input v-model.number="draftShadow.offsetX" type="range" min="-24" max="24" />
                  <strong class="bsm__range-value">{{ draftShadow.offsetX }}px</strong>
                </div>
              </label>

              <label class="bsm__field">
                <span class="bsm__field-label">Offset Y</span>
                <div class="bsm__range-row">
                  <input v-model.number="draftShadow.offsetY" type="range" min="-24" max="24" />
                  <strong class="bsm__range-value">{{ draftShadow.offsetY }}px</strong>
                </div>
              </label>

              <label class="bsm__field">
                <span class="bsm__field-label">Blur</span>
                <div class="bsm__range-row">
                  <input v-model.number="draftShadow.blur" type="range" min="0" max="48" />
                  <strong class="bsm__range-value">{{ draftShadow.blur }}px</strong>
                </div>
              </label>

              <label class="bsm__field">
                <span class="bsm__field-label">Spread</span>
                <div class="bsm__range-row">
                  <input v-model.number="draftShadow.spread" type="range" min="-12" max="24" />
                  <strong class="bsm__range-value">{{ draftShadow.spread }}px</strong>
                </div>
              </label>

              <label class="bsm__field">
                <span class="bsm__field-label">Shadow Colour</span>
                <div class="bsm__color-row">
                  <input v-model="draftShadowHexColor" type="color" class="bsm__color-swatch" />
                  <input
                    v-model="draftShadowHexColor"
                    type="text"
                    class="bsm__color-hex"
                    maxlength="7"
                    placeholder="#000000"
                  />
                </div>
              </label>

              <label class="bsm__field">
                <span class="bsm__field-label">Shadow Opacity</span>
                <div class="bsm__range-row">
                  <input v-model.number="draftShadowOpacity" type="range" min="0" max="100" />
                  <strong class="bsm__range-value">{{ draftShadowOpacity }}%</strong>
                </div>
              </label>
            </div>
          </fieldset>
        </div>

        <!-- Footer -->
        <footer class="bsm__footer-wrapper modal-footer">
          <p v-if="urlError" class="bsm__footer-hint" role="alert">{{ urlError }}</p>
          <div class="bsm__actions">
            <button type="button" class="bsm__action bsm__action--secondary" @click="emit('close')">
              Cancel
            </button>
            <button type="button" class="bsm__action bsm__action--secondary" @click="handleReset">
              Reset
            </button>
            <button
              type="button"
              class="bsm__action bsm__action--primary"
              :disabled="Boolean(urlError)"
              @click="handleSave"
            >
              Save Button
            </button>
          </div>
        </footer>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { computed, reactive, watch } from 'vue'
import type { DropShadowConfig, StyledButtonConfig } from '@/types/form.types'
import {
  buildButtonEditorConfig,
  buildButtonPreviewStyle,
  isValidButtonUrl,
  normalizeStyledButtonConfig,
  normalizeDropShadow,
  stripButtonUrlProtocol,
} from '@/utils/buttonStyles'

const STYLE_VARIANTS = [
  { value: 'solid' as const, label: 'Solid' },
  { value: 'frosted' as const, label: 'Frosted' },
  { value: 'outline' as const, label: 'Outline' },
]

const props = defineProps<{
  show: boolean
  title: string
  description: string
  config?: StyledButtonConfig | null
  fallbackConfig: StyledButtonConfig
  allowUrl?: boolean
  fallbackBackgroundColor: string
  fallbackTextColor: string
  previewBackgroundColor: string
  previewBackgroundImageUrl?: string | null
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'save', value: StyledButtonConfig): void
}>()

const resolveDraftConfig = () =>
  buildButtonEditorConfig(props.config, props.fallbackConfig, {
    backgroundColor: props.fallbackBackgroundColor,
    textColor: props.fallbackTextColor,
    borderColor: props.fallbackBackgroundColor,
  })

const draft = reactive<StyledButtonConfig>(resolveDraftConfig())
const draftShadow = reactive<DropShadowConfig>(normalizeDropShadow(draft.dropShadow))

/**
 * Shadow colour is stored as rgba but the colour picker needs hex + a separate opacity slider.
 * We derive hex/opacity from the rgba string and rebuild rgba on change.
 */
const parseRgbaColor = (rgba: string): { hex: string; opacity: number } => {
  const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/)
  if (match) {
    const r = parseInt(match[1], 10)
    const g = parseInt(match[2], 10)
    const b = parseInt(match[3], 10)
    const a = match[4] !== undefined ? parseFloat(match[4]) : 1
    const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
    return { hex, opacity: Math.round(a * 100) }
  }
  return { hex: rgba.startsWith('#') ? rgba : '#000000', opacity: 35 }
}

const hexToRgba = (hex: string, opacity: number): string => {
  const cleaned = hex.replace('#', '')
  const r = parseInt(cleaned.substring(0, 2), 16) || 0
  const g = parseInt(cleaned.substring(2, 4), 16) || 0
  const b = parseInt(cleaned.substring(4, 6), 16) || 0
  return `rgba(${r},${g},${b},${(opacity / 100).toFixed(2)})`
}

const shadowColorParts = reactive(parseRgbaColor(draftShadow.color))

const draftShadowHexColor = computed({
  get: () => shadowColorParts.hex,
  set: (v: string) => {
    shadowColorParts.hex = v
    draftShadow.color = hexToRgba(v, shadowColorParts.opacity)
  },
})

const draftShadowOpacity = computed({
  get: () => shadowColorParts.opacity,
  set: (v: number) => {
    shadowColorParts.opacity = v
    draftShadow.color = hexToRgba(shadowColorParts.hex, v)
  },
})

const syncDraft = () => {
  const normalized = resolveDraftConfig()
  Object.assign(draft, normalized)
  const normalizedShadow = normalizeDropShadow(normalized.dropShadow)
  Object.assign(draftShadow, normalizedShadow)
  Object.assign(shadowColorParts, parseRgbaColor(normalizedShadow.color))
}

watch(
  () => [props.show, props.config, props.fallbackConfig] as const,
  ([show]) => {
    if (show) {
      syncDraft()
      validateUrl()
    }
  },
  { deep: true, immediate: true },
)

watch(draftShadow, (val) => {
  draft.dropShadow = { ...val }
}, { deep: true })

const urlError = computed(() => {
  if (!props.allowUrl) {
    return ''
  }
  const normalized = stripButtonUrlProtocol(draft.url)
  if (!normalized) {
    return 'Button URL is required.'
  }
  return isValidButtonUrl(normalized) ? '' : 'Enter a valid URL without https://'
})

const validateUrl = () => urlError.value
const normalizeDraftUrl = () => {
  draft.url = stripButtonUrlProtocol(draft.url) || undefined
  validateUrl()
}

const previewLabel = computed(() => draft.label?.trim() || props.fallbackConfig.label || 'Tap here')
const previewStyle = computed(() =>
  buildButtonPreviewStyle(draft, {
    backgroundColor: props.fallbackBackgroundColor,
    textColor: props.fallbackTextColor,
    borderColor: props.fallbackBackgroundColor,
  }, props.fallbackConfig),
)
const previewSurfaceStyle = computed(() => {
  const surfaceColor = props.previewBackgroundColor?.trim() || '#292E50'
  const imageUrl = props.previewBackgroundImageUrl?.trim()

  return {
    backgroundColor: surfaceColor,
    backgroundImage: imageUrl ? `url("${imageUrl}")` : undefined,
    backgroundSize: imageUrl ? '240%' : undefined,
    backgroundPosition: imageUrl ? 'center center' : undefined,
    backgroundRepeat: imageUrl ? 'no-repeat' : undefined,
  }
})

const handleReset = () => {
  syncDraft()
}

const handleSave = () => {
  if (urlError.value) {
    return
  }
  emit('save', normalizeStyledButtonConfig(draft, props.fallbackConfig))
}
</script>

<style scoped>
/* ─── Overlay ─── */
.bsm-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  z-index: calc(var(--base-layout-modal-z-index) + 200);
}

/* ─── Modal shell ─── */
.bsm {
  --bsm-scale: 1;
  width: min(680px, 100%);
  max-height: min(92vh, 860px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--ui-inverse-surface) !important;
  border: none !important;
  box-shadow: none !important;
  --ui-inverse-text: #ffffff;
  --ui-inverse-text-muted: rgba(255, 255, 255, 0.84);
  --ui-inverse-border: rgba(255, 255, 255, 0.18);
  --ui-inverse-border-strong: rgba(255, 255, 255, 0.28);
}

/* ─── Header ─── */
.bsm__header {
  flex-shrink: 0;
  background: var(--ui-inverse-surface) !important;
  border-bottom: none !important;
}

.bsm__header-copy {
  display: grid;
  gap: 0.35rem;
  max-width: min(100%, 28rem);
}

.bsm__header h3 {
  margin-bottom: 0;
  color: #ffffff !important;
}

.bsm__description {
  margin: 0;
  color: #ffffff;
  font-size: 0.92rem;
  line-height: 1.45;
}

/* ─── Sticky preview - fixed height, never grows ─── */
.bsm__preview-sticky {
  flex-shrink: 0;
  padding: 1rem 1.25rem 1.1rem;
  background: var(--ui-inverse-surface);
  border-bottom: none;
}

.bsm__preview-shell {
  min-height: 92px;
  padding: 0.85rem 1rem;
  border-radius: 1rem;
  border: 1px solid rgba(16, 26, 52, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  box-shadow:
    inset 0 0 0 1px rgba(230, 255, 244, 0.08),
    inset 0 0 0 999px rgba(16, 26, 52, 0.08);
}

.bsm__preview-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.9rem 1.4rem;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.2;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 320px;
  transition: all 160ms ease;
  cursor: default;
}

/* ─── Scrollable options ─── */
.bsm__scroll-body {
  flex: 1 1 auto;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0.65rem 1.25rem 1rem;
  background: var(--ui-inverse-surface) !important;
}

/* ─── Group fieldsets ─── */
.bsm__group {
  border: 1px solid var(--ui-inverse-border);
  border-radius: 0.85rem;
  padding: 0.85rem;
  margin: 0 0 0.65rem;
  background: rgba(15, 23, 42, 0.42);
}

.bsm__group-title {
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #ffffff;
  padding: 0 0.3rem;
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

/* ─── Field grid ─── */
.bsm__field-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.65rem;
  margin-top: 0.55rem;
}

.bsm__field {
  display: grid;
  gap: 0.4rem;
  color: #ffffff;
  padding: 0.7rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(15, 23, 42, 0.45);
}

.bsm__field--wide {
  grid-column: 1 / -1;
}


.bsm__field-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.bsm__field input,
.bsm__field select {
  width: 100%;
  min-height: 2.85rem;
  border-radius: 999px;
  padding: 0.7rem 0.9rem;
}

.bsm__field input[type='range'] {
  padding: 0;
  min-height: auto;
  height: 6px;
  -webkit-appearance: none;
  appearance: none;
  background: rgba(255, 255, 255, 0.22);
  border-radius: 3px;
  border: none;
  cursor: pointer;
}

.bsm__field input[type='range']::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--vt-c-accent);
  border: 2px solid var(--color-background);
  cursor: pointer;
}

.bsm__field input[type='range']::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--vt-c-accent);
  border: 2px solid var(--color-background);
  cursor: pointer;
}

/* ─── Colour row (swatch + hex) ─── */
.bsm__color-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.bsm__color-swatch {
  width: 40px !important;
  min-width: 40px;
  height: 40px !important;
  min-height: 40px !important;
  padding: 2px !important;
  border-radius: 0.75rem !important;
  border: 1px solid rgba(255, 255, 255, 0.18) !important;
  cursor: pointer;
  flex-shrink: 0;
}

.bsm__color-hex {
  flex: 1;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.82rem !important;
}

/* ─── Range row ─── */
.bsm__range-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.bsm__range-row input[type='range'] {
  flex: 1;
}

.bsm__range-value {
  min-width: 3rem;
  text-align: right;
  font-size: 0.82rem;
  color: #ffffff;
  font-variant-numeric: tabular-nums;
}

/* ─── Variant pills ─── */
.bsm__variant-pills {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.55rem;
  flex-wrap: wrap;
}

.bsm__variant-pill {
  padding: 0.55rem 1.2rem;
  border-radius: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.24);
  background: rgba(15, 23, 42, 0.45);
  color: #ffffff;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 140ms ease;
  font-family: var(--ui-inverse-font-family);
}

.bsm__variant-pill:hover {
  border-color: var(--vt-c-accent);
  background: rgba(15, 23, 42, 0.7);
  color: #ffffff;
}

.bsm__variant-pill--active {
  background: var(--vt-c-accent);
  border-color: var(--vt-c-accent);
  color: var(--color-background);
}

/* ─── Toggle switch ─── */
.bsm__toggle-label {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}

.bsm__toggle-checkbox {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.bsm__toggle-switch {
  width: 36px;
  height: 20px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.24);
  position: relative;
  transition: background 150ms ease;
}

.bsm__toggle-switch::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #ffffff;
  transition: transform 150ms ease, background 150ms ease;
}

.bsm__toggle-checkbox:checked + .bsm__toggle-switch {
  background: rgba(var(--vt-c-accent-rgb), 0.42);
}

.bsm__toggle-checkbox:checked + .bsm__toggle-switch::after {
  transform: translateX(16px);
  background: var(--vt-c-accent);
}

/* ─── Error ─── */
.bsm__error {
  margin: 0.65rem 0 0;
  color: #8a1027;
}

/* ─── Footer ─── */
.bsm__footer-wrapper {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  background: var(--ui-inverse-surface) !important;
  border-top: none !important;
}

.bsm__footer-hint {
  margin: 0;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.45);
  color: #ffffff;
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.35;
}

.bsm__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.6rem;
  flex-wrap: nowrap;
}

.bsm__actions > button {
  flex: 1 1 0;
}

.bsm__action {
  min-height: 2.85rem;
  margin: 0;
  border-radius: 999px;
  padding: 0.65rem 1rem;
  font-family: var(--ui-inverse-font-family);
  font-weight: 700;
  transition:
    background-color 140ms ease,
    border-color 140ms ease,
    color 140ms ease,
    opacity 140ms ease;
}

.bsm__action--secondary {
  border: 1px solid var(--vt-c-accent);
  background: var(--vt-c-accent);
  color: var(--color-background);
}

.bsm__action--secondary:hover {
  background: var(--ui-inverse-cta-bg-hover);
  border-color: var(--ui-inverse-cta-bg-hover);
}

.bsm__action--primary {
  border: 1px solid var(--vt-c-accent);
  background: var(--vt-c-accent);
  color: var(--color-background);
}

.bsm__action--primary:hover:not(:disabled) {
  background: var(--ui-inverse-cta-bg-hover);
  border-color: var(--ui-inverse-cta-bg-hover);
}

.bsm__action:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

@media (min-width: 769px) {
  .bsm {
    --bsm-scale: calc(var(--desktop-modal-scale, 1) * var(--desktop-display-density-scale, 1));
    width: min(calc(680px * var(--bsm-scale)), 100%);
    max-height: min(92vh, calc(860px * var(--bsm-scale)));
  }

  .bsm__header {
    padding:
      max(1.1rem, calc(1.5rem * var(--bsm-scale)))
      max(2.2rem, calc(3.5rem * var(--bsm-scale)));
  }

  .bsm__header-copy {
    gap: max(0.28rem, calc(0.35rem * var(--bsm-scale)));
    max-width: min(100%, calc(28rem * var(--bsm-scale)));
  }

  .bsm__description {
    font-size: calc(0.92rem * var(--bsm-scale));
  }

  .bsm__preview-sticky {
    padding:
      max(0.8rem, calc(1rem * var(--bsm-scale)))
      max(1rem, calc(1.25rem * var(--bsm-scale)))
      max(0.9rem, calc(1.1rem * var(--bsm-scale)));
  }

  .bsm__preview-shell {
    min-height: calc(92px * var(--bsm-scale));
    padding:
      calc(0.85rem * var(--bsm-scale))
      calc(1rem * var(--bsm-scale));
    border-radius: max(0.85rem, calc(1rem * var(--bsm-scale)));
  }

  .bsm__preview-button {
    padding:
      calc(0.9rem * var(--bsm-scale))
      calc(1.4rem * var(--bsm-scale));
    font-size: calc(1rem * var(--bsm-scale));
    max-width: calc(320px * var(--bsm-scale));
  }

  .bsm__scroll-body {
    padding:
      max(0.5rem, calc(0.65rem * var(--bsm-scale)))
      max(1rem, calc(1.25rem * var(--bsm-scale)))
      max(0.8rem, calc(1rem * var(--bsm-scale)));
  }

  .bsm__group {
    padding: max(0.72rem, calc(0.85rem * var(--bsm-scale)));
    margin-bottom: max(0.55rem, calc(0.65rem * var(--bsm-scale)));
    border-radius: max(0.72rem, calc(0.85rem * var(--bsm-scale)));
  }

  .bsm__group-title {
    font-size: calc(0.78rem * var(--bsm-scale));
    gap: max(0.45rem, calc(0.65rem * var(--bsm-scale)));
  }

  .bsm__field-grid {
    gap: max(0.55rem, calc(0.65rem * var(--bsm-scale)));
    margin-top: max(0.45rem, calc(0.55rem * var(--bsm-scale)));
  }

  .bsm__field {
    gap: max(0.32rem, calc(0.4rem * var(--bsm-scale)));
    padding: max(0.58rem, calc(0.7rem * var(--bsm-scale)));
    border-radius: max(0.65rem, calc(0.75rem * var(--bsm-scale)));
  }

  .bsm__field-label {
    font-size: calc(0.75rem * var(--bsm-scale));
  }

  .bsm__field input,
  .bsm__field select {
    min-height: calc(2.85rem * var(--bsm-scale));
    padding:
      calc(0.7rem * var(--bsm-scale))
      calc(0.9rem * var(--bsm-scale));
  }


  .bsm__color-row,
  .bsm__range-row,
  .bsm__variant-pills,
  .bsm__actions {
    gap: max(0.45rem, calc(0.6rem * var(--bsm-scale)));
  }

  .bsm__color-swatch {
    width: calc(40px * var(--bsm-scale)) !important;
    min-width: calc(40px * var(--bsm-scale));
    height: calc(40px * var(--bsm-scale)) !important;
    min-height: calc(40px * var(--bsm-scale)) !important;
    border-radius: max(0.65rem, calc(0.75rem * var(--bsm-scale))) !important;
  }

  .bsm__color-hex,
  .bsm__range-value,
  .bsm__error {
    font-size: calc(0.82rem * var(--bsm-scale));
  }

  .bsm__variant-pill {
    padding:
      calc(0.55rem * var(--bsm-scale))
      calc(1.2rem * var(--bsm-scale));
    font-size: calc(0.85rem * var(--bsm-scale));
  }

  .bsm__toggle-switch {
    width: calc(36px * var(--bsm-scale));
    height: calc(20px * var(--bsm-scale));
    border-radius: calc(10px * var(--bsm-scale));
  }

  .bsm__toggle-switch::after {
    top: calc(2px * var(--bsm-scale));
    left: calc(2px * var(--bsm-scale));
    width: calc(16px * var(--bsm-scale));
    height: calc(16px * var(--bsm-scale));
  }

  .bsm__toggle-checkbox:checked + .bsm__toggle-switch::after {
    transform: translateX(calc(16px * var(--bsm-scale)));
  }

  .bsm__action {
    min-height: calc(2.85rem * var(--bsm-scale));
    padding:
      calc(0.65rem * var(--bsm-scale))
      calc(1rem * var(--bsm-scale));
    font-size: calc(0.94rem * var(--bsm-scale));
  }
}

@media (min-width: 769px) and (max-height: 900px) {
  .bsm-overlay {
    align-items: flex-start;
    padding: 1.25rem 1rem;
  }
}

/* ─── Responsive ─── */
@media (max-width: 640px) {
  .bsm {
    max-height: min(94vh, 860px);
  }

  .bsm__header,
  .bsm__scroll-body,
  .bsm__actions,
  .bsm__preview-sticky {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .bsm__field-grid {
    grid-template-columns: 1fr;
  }

  .bsm__actions {
    flex-wrap: wrap;
  }
}
</style>
